import { TextLink } from '@edsn/pie-components';
import he from 'he';
import { Trans, useTranslation } from 'react-i18next';
import { Card } from '#pie/components/card/Card';
import {
  DescriptionList,
  DescriptionListItem,
} from '#pie/components/description-list/DescriptionList';
import { InfoTooltip } from '#pie/components/info-tooltip/InfoTooltip';
import { Stack } from '#pie/components/stack/Stack';
import { Text } from '#pie/components/text/Text';
import type { MarketPartyRegistrationDto } from '#pie/internal/api/pie-bff';

interface MarketPartyInvoiceContacProps {
  marketParty: MarketPartyRegistrationDto;
}

const decodeHtmlEntities = (text: string | null | undefined) => {
  return text ? he.decode(text) : '';
};

export const MarketPartyInvoiceContact = ({
  marketParty,
}: MarketPartyInvoiceContacProps) => {
  const { t } = useTranslation();
  const { t: tmp } = useTranslation('marketParty');
  const { t: thl } = useTranslation('hyperlinks');

  return (
    <Card
      size="lg"
      heading={
        <Stack direction="row" gap="sm" align="center">
          <Text as="h2" variant="h6" className="text-content-heading">
            {t('common.invoice_contact.title')}
          </Text>
          <InfoTooltip
            className="text-content-heading"
            aria-label="marketparty information is from the kvk"
            size={20}
          >
            {tmp('invoice_contact.title.tooltip')}
          </InfoTooltip>
        </Stack>
      }
      headingUnderline={false}
    >
      {marketParty.invoiceContacts?.length ? (
        marketParty.invoiceContacts.map((ic, index) => (
          <Stack gap="sm" className="mb-4" key={index}>
            <Card
              size="md"
              heading={
                <Stack
                  direction="row"
                  align="center"
                  className="justify-between"
                >
                  <Text
                    as="h2"
                    variant="base"
                    className="text-content-heading font-bold"
                  >
                    {ic.subjectDescription}
                  </Text>
                </Stack>
              }
            >
              <DescriptionList variant="grid">
                <DescriptionListItem
                  title={t('common.invoice_contact.address')}
                  direction="vertical"
                >
                  <Text>{decodeHtmlEntities(marketParty.statutoryName)}</Text>
                  <Text>
                    {decodeHtmlEntities(ic.addressExtensionDescription)}
                  </Text>
                  <Text>
                    {decodeHtmlEntities(marketParty.streetName)}{' '}
                    {marketParty.houseNumber}
                    {marketParty.houseNumberExtension}
                  </Text>
                  <Text>
                    {marketParty.zipCode} {''}
                    {decodeHtmlEntities(marketParty.city)}
                  </Text>
                  <Text>{decodeHtmlEntities(marketParty.country)}</Text>
                </DescriptionListItem>
                <DescriptionListItem
                  title={t('common.invoice_contact.email')}
                  direction="vertical"
                >
                  {ic.electronicAddressPrimary}
                </DescriptionListItem>
              </DescriptionList>
            </Card>
          </Stack>
        ))
      ) : (
        <Card className="mb-3 p-4">
          <Trans
            i18nKey="invoice_contact.no_invoice_contact"
            t={tmp}
            components={[
              <TextLink
                key={0}
                href={thl('edit.market_party_registration')}
                target="_blank"
                rel="noopener noreferrer"
              />,
            ]}
          />
        </Card>
      )}
    </Card>
  );
};
